import React, { FC, ReactElement } from 'react'
import { styled, breakpointUp } from '@moonpig/launchpad-utils'
import { Box, Container, Flex, Heading } from '@moonpig/launchpad-components'
import { removeSearchHistoryItem } from '@moonpig/web-core-app-shell-search'
import { system as s } from '@moonpig/launchpad-system'
import { DepartmentsEnum } from '@moonpig/web-explore-types-graphql'
import { getDepartmentTitle } from '@moonpig/web-core-utils'
import { spacingPx } from '@moonpig/launchpad-theme'
import { useFindLocaleText } from '../../text-localisation'
import { ContactUsCarousel } from '../ContactUsCarousel'
import type { SearchContext } from '../../store/slices/shared'
import { useSearchStore } from '../../store/SearchStore'
import { DepartmentTabs } from '../DepartmentTabs/DepartmentTabs'
import { getSearchTerm } from './getSearchTerm'
import { capitaliseFirstCharacter } from '../../utils'
import { ZeroResultsHeader } from '../ZeroResultsHeader/ZeroResultsHeader'
import { ResultsCountHeader } from '../ResultsCountHeader'
import { MultiBuyToggle } from '../MultiBuyToggle/MultiBuyToggle'
import { useExperiment } from '@moonpig/web-core-eppo/browser'

const ReservedSpace = styled(Box)`
  height: 64px;
  ${breakpointUp('md')} {
    height: 24px;
  }
`

const StyledContainer = styled(Container)`
  ${s({
    p: 0,
    m: 0,
  })}
`

const StyledHeader = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  ${({ theme }) =>
    s({
      pb: 5,
      gap: spacingPx(3)({ theme }),
    })}

  ${breakpointUp('md')} {
    flex-direction: row;
    align-items: center;
  }
`

export type SearchHeaderProps = {
  zeroResults: boolean
  searchContext: SearchContext
  totalCount?: number
  department: DepartmentsEnum
  rudeToggle?: ReactElement
}

export const SearchHeader: FC<SearchHeaderProps> = ({
  zeroResults,
  searchContext,
  department,
  rudeToggle,
  totalCount,
}) => {
  const loadingProducts = useSearchStore(store => store.loading)
  const isMultiBuySupported = useSearchStore(
    store => store.results.isMultiBuySupported,
  )
  const localiseText = useFindLocaleText()

  const useMultiBuy =
    useExperiment('christmas-multi-buy', {
      fallback: 'control',
    })?.toLowerCase() === 'variant'

  const {
    searchTerm: originalSearchTerm,
    correctedSearchTerm,
    selectedSuggestion,
  } = searchContext

  const { term, isSearchTermBlank } = getSearchTerm({
    selectedSuggestion,
    searchTerm: originalSearchTerm,
    correctedSearchTerm,
  })

  const showControls = !zeroResults || correctedSearchTerm

  if (zeroResults && department && !loadingProducts) {
    removeSearchHistoryItem({
      searchTerm: term,
      department: department.toLowerCase(),
    })
  }

  const departmentName = `${capitaliseFirstCharacter(
    getDepartmentTitle(department),
  )}`

  const showMultiBuyToggle = isMultiBuySupported && useMultiBuy

  return (
    <StyledContainer data-testid="search-header">
      <Box>
        {loadingProducts ? (
          <ReservedSpace />
        ) : (
          <>
            {(zeroResults || correctedSearchTerm) && (
              <ZeroResultsHeader
                originalSearchTerm={originalSearchTerm}
                correctedSearchTerm={correctedSearchTerm}
              />
            )}
            {showControls ? (
              <>
                <StyledHeader>
                  <Box>
                    <Heading
                      level="h1"
                      typography={{
                        xs: 'typeMobileDisplay03',
                        md: 'typeDesktopDisplay03',
                      }}
                      data-testid="search-term-text"
                      mb={0}
                      display="inline"
                    >
                      {isSearchTermBlank
                        ? `${localiseText(
                            'find.search_header_all',
                          )} ${localiseText('find.department', {
                            departmentName,
                          })}`
                        : `"${capitaliseFirstCharacter(term)}"`}
                    </Heading>
                    &nbsp;
                    <ResultsCountHeader count={totalCount} />
                  </Box>
                  {showMultiBuyToggle && <MultiBuyToggle />}

                  {rudeToggle && (
                    <Box display="flex" flexShrink={0} marginLeft="auto">
                      {rudeToggle}
                    </Box>
                  )}
                </StyledHeader>

                <ContactUsCarousel searchTerm={term} />
              </>
            ) : (
              <Heading level="h2">
                {localiseText('find.products_you_may_like')}
              </Heading>
            )}
          </>
        )}
      </Box>
      {showControls && (
        <DepartmentTabs
          currentDepartment={department}
          searchContext={searchContext}
        />
      )}
    </StyledContainer>
  )
}
